import { onMounted, onUnmounted } from 'vue'

export interface SubscriptionOptions {
  immediate?: boolean
}

export interface SubscriptionControl {
  subscribe: () => void
  unsubscribe: () => void
  readonly isSubscribed: boolean
}

/**
 * Composable for managing real-time subscriptions with automatic cleanup
 * Handles the common pattern of subscribing on mount and unsubscribing on unmount
 */
export function useRealtimeSubscription<T>(
  subscribeFn: () => T,
  unsubscribeFn: (subscription: T) => void,
  options: SubscriptionOptions = {}
): SubscriptionControl {
  const { immediate = true } = options

  let subscription: T | null = null

  const subscribe = () => {
    if (subscription === null) {
      subscription = subscribeFn()
    }
  }

  const unsubscribe = () => {
    if (subscription !== null) {
      unsubscribeFn(subscription)
      subscription = null
    }
  }

  if (immediate) {
    onMounted(subscribe)
  }

  onUnmounted(unsubscribe)

  // Return control functions for manual management if needed
  return {
    subscribe,
    unsubscribe,
    get isSubscribed() {
      return subscription !== null
    }
  }
}

export interface MultiSubscriptionControl<T extends Record<string, { subscribe: () => unknown; unsubscribe: (sub: unknown) => void }>> {
  subscribeAll: () => void
  unsubscribeAll: () => void
  subscribe: (key: keyof T) => void
  unsubscribe: (key: keyof T) => void
}

/**
 * Composable for managing multiple subscriptions
 */
export function useRealtimeSubscriptions<T extends Record<string, { subscribe: () => unknown; unsubscribe: (sub: unknown) => void }>>(
  subscriptions: T,
  options: SubscriptionOptions = {}
): MultiSubscriptionControl<T> {
  const { immediate = true } = options

  const subscriptionRefs: Partial<Record<keyof T, any>> = {}

  const subscribeAll = () => {
    for (const [key, subConfig] of Object.entries(subscriptions) as [keyof T, { subscribe: () => any; unsubscribe: (sub: any) => void }][]) {
      if (subscriptionRefs[key] === undefined) {
        subscriptionRefs[key] = subConfig.subscribe()
      }
    }
  }

  const unsubscribeAll = () => {
    for (const [key, subConfig] of Object.entries(subscriptions) as [keyof T, { subscribe: () => any; unsubscribe: (sub: any) => void }][]) {
      if (subscriptionRefs[key] !== undefined) {
        subConfig.unsubscribe(subscriptionRefs[key])
        subscriptionRefs[key] = undefined
      }
    }
  }

  if (immediate) {
    onMounted(subscribeAll)
  }

  onUnmounted(unsubscribeAll)

  // Return control functions
  return {
    subscribeAll,
    unsubscribeAll,
    subscribe: (key: keyof T) => {
      if (subscriptionRefs[key] === undefined) {
        subscriptionRefs[key] = subscriptions[key].subscribe()
      }
    },
    unsubscribe: (key: keyof T) => {
      if (subscriptionRefs[key] !== undefined) {
        subscriptions[key].unsubscribe(subscriptionRefs[key])
        subscriptionRefs[key] = undefined
      }
    }
  }
}